import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import "katex/dist/katex.min.css";
import { BlockMath, InlineMath } from "react-katex";
import { renderWithLatex } from "../utils/latexRenderer";   // ← shared utility

const API_BASE = "https://eluvent.com/api";

const DIFFICULTY_COLORS = {
  easy: { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0", label: "🟢 Easy" },
  medium: { bg: "#fef3c7", text: "#92400e", border: "#fcd34d", label: "🟡 Medium" },
  hard: { bg: "#fef2f2", text: "#991b1b", border: "#fecaca", label: "🔴 Hard" }
};

const TYPE_ICONS = {
  mcq: "🔘", numerical: "🔢", theory: "📝", assertion_reason: "🔗",
  case_based: "📋", diagram_based: "📐", fill_blanks: "✍️",
  true_false: "✅", match_the_following: "🔗"
};

const Badge = ({ children, bg = "#f1f5f9", color = "#475569", style = {} }) => (
  <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: color, ...style }}>{children}</span>
);

const LoadingSpinner = ({ text = "Loading..." }) => (
  <div style={{ textAlign: "center", padding: "40px 20px" }}>
    <div style={{ width: 40, height: 40, border: "4px solid #e2e8f0", borderTop: "4px solid #6366f1", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }}></div>
    <p style={{ color: "#64748b", fontSize: 14, fontWeight: 600 }}>{text}</p>
  </div>
);

const ErrorMessage = ({ message, onRetry }) => (
  <div style={{ textAlign: "center", padding: "30px 20px" }}>
    <p style={{ fontSize: 40, marginBottom: 8 }}>😕</p>
    <p style={{ color: "#ef4444", fontSize: 14, fontWeight: 600 }}>{message}</p>
    {onRetry && <button onClick={onRetry} style={{ marginTop: 12, padding: "8px 20px", background: "#6366f1", color: "white", border: "none", borderRadius: 20, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>🔄 Try Again</button>}
  </div>
);

const Breadcrumbs = ({ items }) => (
  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", marginBottom: 16, fontSize: 12 }}>
    {items.map((item, i) => (
      <span key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {i > 0 && <span style={{ color: "#cbd5e1" }}>›</span>}
        {item.url ? <Link to={item.url} style={{ color: "#6366f1", textDecoration: "none", fontWeight: 600 }}>{item.label}</Link> : <span style={{ color: "#475569", fontWeight: 700 }}>{item.label}</span>}
      </span>
    ))}
  </div>
);

const SectionCard = ({ title, icon, bg, border, children }) => (
  <div style={{ background: bg || "white", borderRadius: 16, padding: "20px", marginBottom: 16, border: `1px solid ${border || "#e2e8f0"}` }}>
    {title && <h4 style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}>{icon} {title}</h4>}
    {children}
  </div>
);

const renderText = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => <span key={i}>{line}{i < text.split('\n').length - 1 && <br />}</span>);
};

// ============================================================
// QUESTION DETAIL COMPONENT
// ============================================================
const QuestionDetail = () => {
  const { id: questionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ---------- Navigation state from BoardVault ----------
  const questionIds = location.state?.questionIds || [];
  const currentIndex = location.state?.currentIndex || 0;
  const board = location.state?.board || "";
  const subject = location.state?.subject || "";

  const prevId = currentIndex > 0 ? questionIds[currentIndex - 1] : null;
  const nextId = currentIndex < questionIds.length - 1 ? questionIds[currentIndex + 1] : null;

  // ---------- Bookmark ----------
  const [bookmarked, setBookmarked] = useState(() => {
    return localStorage.getItem(`bookmark_${questionId}`) === "true";
  });
  const toggleBookmark = () => {
    const next = !bookmarked;
    setBookmarked(next);
    localStorage.setItem(`bookmark_${questionId}`, next.toString());
  };

  // ---------- Share ----------
  const [showShareMsg, setShowShareMsg] = useState(false);
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setShowShareMsg(true);
      setTimeout(() => setShowShareMsg(false), 2000);
    });
  };

  // ---------- Question state ----------
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [explainError, setExplainError] = useState(null);
  const [similarQuestions, setSimilarQuestions] = useState(null);
  const [generatingSimilar, setGeneratingSimilar] = useState(false);
  const [similarError, setSimilarError] = useState(null);
  const [generatingTest, setGeneratingTest] = useState(false);
  const [testError, setTestError] = useState(null);
  const [practiceMode, setPracticeMode] = useState(false);
  const [practiceTest, setPracticeTest] = useState(null);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [testScore, setTestScore] = useState(null);

  const fetchQuestion = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_BASE}/vault/question/${questionId}`);
      if (res.data.question) setQuestion(res.data.question);
      else setError("Question not found.");
    } catch (err) { setError("Could not load question."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQuestion(); }, [questionId]);

  // ---------- AI Explanation ----------
  const handleExplain = async () => {
    setExplaining(true); setExplainError(null); setExplanation(null);
    try {
      const res = await axios.post(`${API_BASE}/vault/question/${questionId}/explain`, { language: "english" });
      if (res.data.explanation) setExplanation(res.data.explanation);
      else setExplainError(res.data.error || "Could not generate explanation.");
    } catch (err) { setExplainError("Failed to generate explanation."); }
    finally { setExplaining(false); }
  };

  // ---------- Similar Questions ----------
  const handleGenerateSimilar = async () => {
    setGeneratingSimilar(true); setSimilarError(null); setSimilarQuestions(null);
    try {
      const res = await axios.post(`${API_BASE}/vault/question/${questionId}/similar`, { count: 5 });
      if (res.data.similar_questions) setSimilarQuestions(res.data.similar_questions);
      else setSimilarError(res.data.error || "Could not generate similar questions.");
    } catch (err) { setSimilarError("Failed to generate similar questions."); }
    finally { setGeneratingSimilar(false); }
  };

  // ---------- Practice Test ----------
  const handleGenerateTest = async () => {
    setGeneratingTest(true); setTestError(null); setPracticeTest(null);
    try {
      const res = await axios.post(`${API_BASE}/vault/practice-test`, { question_id: parseInt(questionId), num_questions: 10, difficulty: question?.difficulty });
      if (res.data.questions && res.data.questions.length > 0) {
        setPracticeTest(res.data); setPracticeMode(true); setCurrentQIndex(0);
        setUserAnswers({}); setTestSubmitted(false); setTestScore(null);
      } else setTestError(res.data.error || "Could not generate practice test.");
    } catch (err) { setTestError("Failed to generate practice test."); }
    finally { setGeneratingTest(false); }
  };

  const handleAnswerSelect = (qIndex, answer) => setUserAnswers(prev => ({ ...prev, [qIndex]: answer }));
  const handleNextQuestion = () => { if (currentQIndex < (practiceTest?.questions?.length || 0) - 1) setCurrentQIndex(prev => prev + 1); };
  const handlePrevQuestion = () => { if (currentQIndex > 0) setCurrentQIndex(prev => prev - 1); };

  const handleSubmitTest = () => {
    const total = practiceTest?.questions?.length || 0;
    let correct = 0;
    const results = practiceTest.questions.map((q, i) => {
      const userAnswer = userAnswers[i];
      const isCorrect = userAnswer === q.solution || userAnswer === q.correct_answer || userAnswer === q.correct;
      if (isCorrect) correct++;
      return { question: q.question_text || q.question, userAnswer, correctAnswer: q.solution || q.correct_answer || q.correct, isCorrect };
    });
    setTestScore({ correct, total, percentage: Math.round((correct / total) * 100), results });
    setTestSubmitted(true);
  };

  if (loading) return <LoadingSpinner text="Loading question..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchQuestion} />;
  if (!question) return <ErrorMessage message="Question not found." />;

  const diffStyle = DIFFICULTY_COLORS[question.difficulty] || DIFFICULTY_COLORS.medium;

  // ============================================================
  // PRACTICE MODE
  // ============================================================
  if (practiceMode && practiceTest) {
    const currentQ = practiceTest.questions[currentQIndex];
    const totalQs = practiceTest.questions.length;

    if (testSubmitted && testScore) {
      return (
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "20px 16px", fontFamily: "'Nunito', sans-serif", background: "#f8fafc", minHeight: "100vh" }}>
          <SectionCard title="📝 Test Results" bg="#f0fdf4" border="#bbf7d0">
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <p style={{ fontSize: 48, margin: "0 0 8px" }}>{testScore.percentage >= 80 ? "🎉" : testScore.percentage >= 60 ? "👍" : "💪"}</p>
              <h3 style={{ fontSize: 24, fontWeight: 800, color: "#1e293b", margin: "0 0 4px" }}>{testScore.correct} / {testScore.total}</h3>
              <p style={{ fontSize: 16, color: "#64748b", margin: 0 }}>{testScore.percentage}% Accuracy</p>
              <p style={{ fontSize: 13, color: "#94a3b8", margin: "8px 0 0" }}>{testScore.percentage >= 80 ? "Excellent work!" : testScore.percentage >= 60 ? "Good effort!" : "Keep practicing!"}</p>
            </div>
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", margin: "0 0 12px" }}>📋 Detailed Results</h4>
              {testScore.results.map((r, i) => (
                <div key={i} style={{ padding: "12px", marginBottom: 8, borderRadius: 10, background: r.isCorrect ? "#f0fdf4" : "#fef2f2", border: `1px solid ${r.isCorrect ? "#bbf7d0" : "#fecaca"}` }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: r.isCorrect ? "#166534" : "#991b1b", margin: "0 0 4px" }}>{r.isCorrect ? "✅ Correct" : "❌ Incorrect"} — Q{i + 1}</p>
                  <p style={{ fontSize: 12, color: "#334155", margin: "0 0 4px" }}>{r.question?.substring(0, 150)}...</p>
                  {!r.isCorrect && <p style={{ fontSize: 11, color: "#059669", margin: 0 }}>✅ Correct Answer: {r.correctAnswer}</p>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "center" }}>
              <button onClick={() => { setPracticeMode(false); setPracticeTest(null); setTestSubmitted(false); }} style={{ padding: "10px 20px", background: "#64748b", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>← Back to Question</button>
              <button onClick={handleGenerateTest} style={{ padding: "10px 20px", background: "#6366f1", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>🔄 New Test</button>
            </div>
          </SectionCard>
        </div>
      );
    }

    return (
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "20px 16px", fontFamily: "'Nunito', sans-serif", background: "#f8fafc", minHeight: "100vh" }}>
        <SectionCard title={`📝 Practice Test — Question ${currentQIndex + 1} of ${totalQs}`} bg="white" border="#e2e8f0">
          <div style={{ background: "#e2e8f0", borderRadius: 10, height: 6, marginBottom: 20 }}>
            <div style={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: 10, height: "100%", width: `${((currentQIndex + 1) / totalQs) * 100}%`, transition: "width 0.3s ease" }}></div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <Badge bg={DIFFICULTY_COLORS[currentQ.difficulty]?.bg || "#f1f5f9"} color={DIFFICULTY_COLORS[currentQ.difficulty]?.text || "#475569"} style={{ marginBottom: 8 }}>
              {DIFFICULTY_COLORS[currentQ.difficulty]?.label || currentQ.difficulty}
            </Badge>
            <p style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{renderWithLatex(currentQ.question_text || currentQ.question)}</p>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "#475569", display: "block", marginBottom: 8 }}>✍️ Your Answer:</label>
            <textarea value={userAnswers[currentQIndex] || ""} onChange={(e) => handleAnswerSelect(currentQIndex, e.target.value)} placeholder="Type your answer here..." rows={4} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "2px solid #e2e8f0", fontSize: 13, fontFamily: "'Nunito', sans-serif", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <button onClick={handlePrevQuestion} disabled={currentQIndex === 0} style={{ padding: "8px 18px", background: currentQIndex === 0 ? "#cbd5e1" : "#6366f1", color: "white", border: "none", borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: currentQIndex === 0 ? "not-allowed" : "pointer" }}>← Previous</button>
            {currentQIndex < totalQs - 1 ? (
              <button onClick={handleNextQuestion} style={{ padding: "8px 18px", background: "#6366f1", color: "white", border: "none", borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Next →</button>
            ) : (
              <button onClick={handleSubmitTest} style={{ padding: "10px 24px", background: "#10b981", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✅ Submit Test</button>
            )}
          </div>
          <div style={{ textAlign: "center", marginTop: 12 }}><span style={{ fontSize: 11, color: "#94a3b8" }}>{Object.keys(userAnswers).length} of {totalQs} answered</span></div>
        </SectionCard>
        <button onClick={() => setPracticeMode(false)} style={{ padding: "10px 20px", background: "#64748b", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 8 }}>Exit Practice</button>
      </div>
    );
  }

  // ============================================================
  // QUESTION DETAIL VIEW
  // ============================================================
  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "20px 16px", fontFamily: "'Nunito', sans-serif", background: "#f8fafc", minHeight: "100vh" }}>
      <SEO
        title={`Q${question.question_number} – ${question.subject_name} | ${question.board_name}`}
        description={question.question_text?.replace(/<\/?[^>]+(>|$)/g, "").substring(0, 155)}
        canonical={window.location.href}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <Link
          to={`/board-vault/${board}/${subject}`}
          style={{ color: "#6366f1", textDecoration: "none", fontWeight: 700, fontSize: 13 }}
        >
          ← Back to {subject || "Subject"}
        </Link>
        <div style={{ display: "flex", gap: 8 }}>
          <button disabled={!prevId} onClick={() => navigate(`/board-vault/question/${prevId}`, { state: { questionIds, currentIndex: currentIndex - 1, board, subject } })}
            style={{ padding: "6px 15px", borderRadius: 20, border: "1px solid #6366f1", background: "white", color: prevId ? "#6366f1" : "#cbd5e1", cursor: prevId ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13 }}
          >← Previous</button>
          <button disabled={!nextId} onClick={() => navigate(`/board-vault/question/${nextId}`, { state: { questionIds, currentIndex: currentIndex + 1, board, subject } })}
            style={{ padding: "6px 15px", borderRadius: 20, border: "1px solid #6366f1", background: "white", color: nextId ? "#6366f1" : "#cbd5e1", cursor: nextId ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13 }}
          >Next →</button>
        </div>
      </div>

      {question.breadcrumbs && <Breadcrumbs items={question.breadcrumbs} />}

      <SectionCard bg="white" border="#e2e8f0">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <Badge bg={diffStyle.bg} color={diffStyle.text}>{diffStyle.label}</Badge>
          <Badge bg="#eff6ff" color="#1e40af">{TYPE_ICONS[question.question_type] || "📄"} {question.question_type?.replace(/_/g, ' ')}</Badge>
          <Badge bg="#f5f3ff" color="#6d28d9">{question.marks} mark{question.marks > 1 ? 's' : ''}</Badge>
          <Badge bg="#fef2f2" color="#991b1b">📅 {question.year}</Badge>
          {question.set_name && <Badge bg="#f0fdf4" color="#166534">Set: {question.set_name}</Badge>}
          {question.source && <Badge bg="#f8fafc" color="#64748b">📄 {question.source}</Badge>}
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", margin: "0 0 4px" }}>Question {question.question_number}</h3>
        <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 12px" }}>{question.board_name} • {question.subject_name} • {question.chapter_name}</p>

        {question.has_diagram && question.diagram_url ? (
          <div style={{ marginBottom: 14, borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "white" }}>
            <img src={question.diagram_url} alt="Question diagram" style={{ width: "100%", maxWidth: "100%", display: "block" }}
              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style=\"background:linear-gradient(135deg,#fef3c7,#fef9c3);border-radius:12px;padding:14px 16px;border:2px solid #fcd34d;display:flex;gap:10px;align-items:center\"><span style=\"font-size:28px\">📐</span><div><p style=\"margin:0;font-size:13px;font-weight:700;color:#92400e\">Diagram not available yet</p><p style=\"margin:4px 0 0;font-size:12px;color:#a16207;line-height:1.5\">Grab a <strong>pencil and paper</strong> — all the details are in the question text. Drawing it yourself is great practice! ✏️</p></div></div>'; }}
            />
          </div>
        ) : question.has_diagram && (
          <div style={{ background: "linear-gradient(135deg, #fef3c7, #fef9c3)", borderRadius: 12, padding: "14px 16px", marginBottom: 14, border: "2px solid #fcd34d", display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 28, flexShrink: 0 }}>📐</span>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#92400e" }}>This question includes a diagram</p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#a16207", lineHeight: 1.5 }}>Grab a <strong>pencil and paper</strong> — all the details are in the question text. Drawing it yourself is great practice! ✏️</p>
            </div>
          </div>
        )}

        <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
          {renderWithLatex(question.question_text)}
        </div>

        {question.tags?.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 12 }}>
            {question.tags.map((tag, i) => <Badge key={i} bg="#f1f5f9" color="#64748b" style={{ fontSize: 10 }}>{tag}</Badge>)}
          </div>
        )}

        {question.related_concepts?.length > 0 && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#f5f3ff", borderRadius: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6d28d9" }}>🧠 Related Concepts: </span>
            {question.related_concepts.map((c, i) => <Badge key={i} bg="#ede9fe" color="#5b21b6" style={{ margin: "2px 3px", fontSize: 10 }}>{c}</Badge>)}
          </div>
        )}
      </SectionCard>

      {question.official_solution && (
        <SectionCard title="✅ Official Solution" bg="#f0fdf4" border="#bbf7d0">
          <div style={{ fontSize: 13, color: "#14532d", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{renderWithLatex(question.official_solution)}</div>
          {question.marking_scheme_points > 0 && <p style={{ fontSize: 11, color: "#15803d", marginTop: 10, fontWeight: 600 }}>📝 {question.marking_scheme_points} marking points in official scheme</p>}
        </SectionCard>
      )}

      {question.examiner_note && (
        <SectionCard title="📋 Examiner's Note" bg="#fef9c3" border="#fde047">
          <div style={{ fontSize: 13, color: "#713f12", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{renderWithLatex(question.examiner_note)}</div>
        </SectionCard>
      )}

      <div style={{ background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)", borderRadius: 16, padding: "14px 18px", marginTop: 20, marginBottom: 0, border: "1px solid #bae6fd", fontSize: 13, color: "#0c4a6e", lineHeight: 1.5 }}>
        <strong style={{ fontSize: 15 }}>📘 Need help with this question?</strong>
        <p style={{ margin: "6px 0 0" }}>If the question looks incomplete or you're stuck, use the tools below:</p>
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          <li>🤖 <strong>AI Explain</strong> – step‑by‑step guidance.</li>
          <li>🔍 <strong>Similar Questions</strong> – practice with variants.</li>
          <li>📝 <strong>Practice Test</strong> – generate a full test on this topic.</li>
        </ul>
        <p style={{ margin: "6px 0 0", fontStyle: "italic" }}>We're constantly improving our content. Every question is hand‑picked from official board papers.</p>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <button onClick={toggleBookmark} style={toolBtnStyle}>{bookmarked ? "🔖 Bookmarked" : "📑 Bookmark"}</button>
        <button onClick={handleShare} style={toolBtnStyle}>📤 Share</button>
        {showShareMsg && <span style={{ color: "#059669", fontSize: 12, fontWeight: 600, alignSelf: "center" }}>Link copied!</span>}
      </div>

      <SectionCard title="🤖 AI Explanation" bg="white" border="#e2e8f0">
        {!explanation && !explaining && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>Get a step-by-step explanation of this question</p>
            <button onClick={handleExplain} style={{ padding: "10px 20px", background: "#6366f1", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>🚀 Generate Explanation</button>
          </div>
        )}
        {explaining && <LoadingSpinner text="Generating AI explanation..." />}
        {explainError && <ErrorMessage message={explainError} onRetry={handleExplain} />}
        {explanation && (
          <div>
            <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.8, whiteSpace: "pre-wrap", background: "#f8fafc", padding: "16px", borderRadius: 12 }}>{renderWithLatex(explanation)}</div>
            <button onClick={handleExplain} style={{ padding: "6px 14px", background: "#64748b", color: "white", border: "none", borderRadius: 25, fontSize: 11, fontWeight: 700, cursor: "pointer", marginTop: 12 }}>🔄 Regenerate</button>
          </div>
        )}
      </SectionCard>

      <SectionCard title="🔄 Similar Questions" bg="white" border="#e2e8f0">
        {!similarQuestions && !generatingSimilar && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>Practice with AI-generated similar questions</p>
            <button onClick={handleGenerateSimilar} style={{ padding: "10px 20px", background: "#10b981", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✨ Generate Similar Questions</button>
          </div>
        )}
        {generatingSimilar && <LoadingSpinner text="Generating similar questions..." />}
        {similarError && <ErrorMessage message={similarError} onRetry={handleGenerateSimilar} />}
        {similarQuestions && (
          <div>
            {similarQuestions.map((q, i) => (
              <div key={i} style={{ padding: "14px", marginBottom: 10, background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", margin: "0 0 6px" }}>Q{i + 1}. {renderWithLatex(q.question_text)}</p>
                {q.solution && (
                  <details style={{ fontSize: 12, color: "#475569" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 600, color: "#059669" }}>✅ View Solution</summary>
                    <div style={{ marginTop: 8, padding: "10px", background: "#f0fdf4", borderRadius: 8 }}>{renderWithLatex(q.solution)}</div>
                  </details>
                )}
                {q.explanation && <p style={{ fontSize: 11, color: "#64748b", margin: "6px 0 0", fontStyle: "italic" }}>💡 {q.explanation}</p>}
              </div>
            ))}
            <button onClick={handleGenerateSimilar} style={{ padding: "6px 14px", background: "#64748b", color: "white", border: "none", borderRadius: 25, fontSize: 11, fontWeight: 700, cursor: "pointer", marginTop: 8 }}>🔄 Generate More</button>
          </div>
        )}
      </SectionCard>

      <SectionCard title="📝 Practice Test" bg="white" border="#e2e8f0">
        {!practiceTest && !generatingTest && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>Test yourself with questions on this topic</p>
            <button onClick={handleGenerateTest} style={{ padding: "10px 20px", background: "#f59e0b", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>🎯 Start Practice Test</button>
          </div>
        )}
        {generatingTest && <LoadingSpinner text="Creating practice test..." />}
        {testError && <ErrorMessage message={testError} onRetry={handleGenerateTest} />}
      </SectionCard>

      {question.total_attempts > 0 && (
        <SectionCard title="📊 Question Statistics" bg="white" border="#e2e8f0">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
            <div style={{ textAlign: "center", padding: "12px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}><p style={{ fontSize: 20, margin: "0 0 4px" }}>📝</p><p style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", margin: "0 0 2px" }}>{question.total_attempts}</p><p style={{ fontSize: 10, color: "#94a3b8", margin: 0, fontWeight: 600 }}>Total Attempts</p></div>
            <div style={{ textAlign: "center", padding: "12px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}><p style={{ fontSize: 20, margin: "0 0 4px" }}>✅</p><p style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", margin: "0 0 2px" }}>{question.avg_score_percent}%</p><p style={{ fontSize: 10, color: "#94a3b8", margin: 0, fontWeight: 600 }}>Avg Score</p></div>
            <div style={{ textAlign: "center", padding: "12px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}><p style={{ fontSize: 20, margin: "0 0 4px" }}>🔥</p><p style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", margin: "0 0 2px" }}>{question.frequency_score}%</p><p style={{ fontSize: 10, color: "#94a3b8", margin: 0, fontWeight: 600 }}>Frequency</p></div>
            {question.predicted_probability > 0 && <div style={{ textAlign: "center", padding: "12px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}><p style={{ fontSize: 20, margin: "0 0 4px" }}>🎯</p><p style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", margin: "0 0 2px" }}>{question.predicted_probability}%</p><p style={{ fontSize: 10, color: "#94a3b8", margin: 0, fontWeight: 600 }}>Predicted</p></div>}
          </div>
        </SectionCard>
      )}

      {question.related_questions?.length > 0 && (
        <SectionCard title="📚 Related Questions" bg="white" border="#e2e8f0">
          {question.related_questions.map(q => (
            <div key={q.id} onClick={() => navigate(`/board-vault/question/${q.id}`)} style={{ padding: "12px", marginBottom: 8, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "#818cf8"} onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#1e293b", fontWeight: 600 }}>{q.question_text?.substring(0, 100)}...</span>
                <div style={{ display: "flex", gap: 4 }}>
                  <Badge bg={DIFFICULTY_COLORS[q.difficulty]?.bg} color={DIFFICULTY_COLORS[q.difficulty]?.text} style={{ fontSize: 10 }}>{q.difficulty}</Badge>
                  <Badge style={{ fontSize: 10 }}>{q.marks}m</Badge>
                </div>
              </div>
            </div>
          ))}
        </SectionCard>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "center" }}>
        <Link to={`/board-vault/${board}/${subject}`} style={{ padding: "10px 20px", background: "#64748b", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>← Back to {subject || "Subject"}</Link>
        <Link to="/board-vault" style={{ padding: "10px 20px", background: "#6366f1", color: "white", border: "none", borderRadius: 25, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>📚 Board Vault Home</Link>
      </div>
    </div>
  );
};

const toolBtnStyle = {
  padding: "8px 16px",
  borderRadius: 25,
  border: "1px solid #e2e8f0",
  background: "white",
  color: "#1e293b",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 12
};

export default QuestionDetail;